import { FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import PhotoDetailsWrapper from '../PhotoDetailsWrapper';
import FirmDetailsWrapper from '../FirmDetailsWrapper';
import MobilePhotoWrapper from 'dibs-interiors-photos/exports/MobilePhotoWrapper';
import PhotoDetailsSection from 'dibs-interiors-photos/exports/PhotoDetailsSection';
import PhotoRailContainer from 'dibs-interiors-photos/exports/PhotoRailContainer';
import PhotoDotsIndicator from 'dibs-interiors-photos/exports/PhotoDotsIndicator';
import MobilePhotoHeader from 'dibs-interiors-photos/exports/MobilePhotoHeader';
import { trackSavePhoto } from 'dibs-interiors-photos/exports/trackingHelpers';
import { getShoppablePhotoData } from 'dibs-interiors-photos/exports/getShoppablePhotoData';
import DotsWrapper from '../../sharedComponents/interiors/DotsWrapper';
import PhotoSwiperWrapper from './PhotoSwiperWrapper';
import SocialIconsWrapper from '../../sharedComponents/interiors/SocialIconsWrapper';

import type { Photo } from 'dibs-interiors-photos/exports/types';
import type { MobilePhotoContent_photoNode$key } from './__generated__/MobilePhotoContent_photoNode.graphql';

type Props = {
    photoNode: MobilePhotoContent_photoNode$key | null;
    onPageChange: (url: string) => void;
    photos: Photo[];
    isModal: boolean;
    onCloseModal?: () => void;
};

const MobilePhotoContent: FC<Props> = props => {
    const { photoNode: photoNodeRef, photos, isModal, onCloseModal, onPageChange } = props;

    const photoNode = useFragment(
        graphql`
            fragment MobilePhotoContent_photoNode on TradePhotosQueryOutput {
                ...PhotoSwiperWrapper_photoNode
                ...PhotoDetailsWrapper_photoNode
                ...FirmDetailsWrapper_photoNode
                firmName
                projectName
                projectUrl
                photo {
                    ...PhotoRailContainer_tradePhoto
                    ...getShoppablePhotoData_tradePhoto
                    id
                    path
                    url
                    serviceId
                    photoLike {
                        photoLikeId(userId: $userId) @include(if: $hasUserId)
                        totalPhotoLikes
                    }
                }
            }
        `,
        photoNodeRef
    );

    const photo = photoNode?.photo;
    const photoId = photo?.serviceId || '';
    const photoIndex = photos.findIndex(_photo => _photo?.serviceId === photo?.serviceId);
    const photoCount = photos.length;
    const projectName = photoNode?.projectName || '';
    const photoLike = photo?.photoLike;

    if (!photo?.path) {
        return null;
    }

    const { isShoppable } = getShoppablePhotoData({ tradePhoto: photo });

    return (
        <>
            {isModal && <MobilePhotoHeader title={projectName} onCloseModal={onCloseModal} />}
            <MobilePhotoWrapper>
                <PhotoSwiperWrapper
                    photos={photos}
                    photoNode={photoNode}
                    onPageChange={onPageChange}
                    photoId={photoId}
                    photoIndex={photoIndex}
                />
                <SocialIconsWrapper
                    additionalSpacing={isModal && !isShoppable}
                    photoNodeId={photo?.id}
                    photoId={photo?.serviceId || ''}
                    photoLikeId={photoLike?.photoLikeId || ''}
                    totalPhotoLikes={photoLike?.totalPhotoLikes || 0}
                    onPhotoLikeAdded={() => trackSavePhoto({ label: 'photo' })}
                    firmName={photoNode?.firmName || ''}
                    projectName={photoNode?.projectName || ''}
                    projectUrl={photoNode?.projectUrl || ''}
                    photoUrl={photo?.url || ''}
                    photoPath={photo?.path || ''}
                />
            </MobilePhotoWrapper>
            <DotsWrapper>
                <PhotoDotsIndicator currentIndex={photoIndex} totalItems={photoCount} />
            </DotsWrapper>
            <PhotoDetailsWrapper photoNode={photoNode} />
            <PhotoDetailsSection>
                <FirmDetailsWrapper photoNode={photoNode} />
            </PhotoDetailsSection>
            <PhotoRailContainer tradePhoto={photo} />
        </>
    );
};

export default MobilePhotoContent;
